import React, { useRef } from 'react';
import { useInteractOutside, useKeyboard } from 'react-aria';

export type ChipSelectionClickHandler = (
    event: React.MouseEvent<HTMLSpanElement>,
    /** Moves keyboard focus onto the chip root (used after a click-select). */
    focusChip: () => void,
) => void;

type UseChipSelectionInteractionsProps = {
    isSelected: boolean;
    canActivate: boolean;
    onClick: ChipSelectionClickHandler;
    onDeselect: () => void;
    /** Deletes the chip's data on Delete/Backspace while selected. Omit when not deletable. */
    onDelete?: () => void;
    /** Toggles the chip's inline editor on Enter/F2 while selected. */
    onEditingToggle?: () => void;
};

/**
 * Returns whether an interaction target belongs to a surface opened from a selected chip.
 */
export const isChipSelectionInteractionTarget = (target: Element | null): boolean =>
    Boolean(target?.closest('.RomaPopover, [data-chip-selection-interaction]'));

/**
 * Provides shared root interactions for select-mode chips.
 */
export const useChipSelectionInteractions = ({
    isSelected,
    canActivate,
    onClick,
    onDeselect,
    onDelete,
    onEditingToggle,
}: UseChipSelectionInteractionsProps) => {
    const chipRef = useRef<HTMLSpanElement | null>(null);

    useInteractOutside({
        ref: chipRef,
        isDisabled: !isSelected,
        onInteractOutside: (event) => {
            const target = event.target as Node | null;
            const elementTarget = target instanceof Element ? target : target?.parentElement;
            if (!elementTarget?.closest('.canvas-section')) return;
            if (isChipSelectionInteractionTarget(elementTarget)) return;

            onDeselect();
        },
    });

    const { keyboardProps } = useKeyboard({
        onKeyDown: (event) => {
            if (!isSelected) {
                event.continuePropagation();
                return;
            }

            // Keys pressed inside the chip's popup (portalled out of this wrapper,
            // but bubbling here through the React tree — e.g. the assignee search
            // field) belong to the popup, not chip selection.
            const target = event.target as Node | null;
            if (target && !chipRef.current?.contains(target)) {
                event.continuePropagation();
                return;
            }

            if (event.key === 'Escape') {
                event.preventDefault();
                onDeselect();
                return;
            }

            if ((event.key === 'Delete' || event.key === 'Backspace') && onDelete) {
                event.preventDefault();
                onDelete();
                return;
            }

            if ((event.key === 'Enter' || event.key === 'F2') && onEditingToggle) {
                event.preventDefault();
                onEditingToggle();
                return;
            }

            event.continuePropagation();
        },
    });

    return {
        chipRef,
        chipProps: {
            role: 'button' as const,
            tabIndex: canActivate ? 0 : -1,
            onClick: (event: React.MouseEvent<HTMLSpanElement>) => {
                // This click selects the chip, not the element beneath it.
                // Stop it before the selection update can enable an editor's click handler.
                if (canActivate) event.stopPropagation();

                onClick(event, () => chipRef.current?.focus());
            },
            onDoubleClick: (event: React.MouseEvent<HTMLSpanElement>) => event.stopPropagation(),
            className: 'outline-none',
            ...keyboardProps,
        },
    };
};
